import { IonMenu } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { FINAL_DRAWER_WIDTH } from '../Drawer';
import { mediaMaxWidth } from '../../theme/utils';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { useEffect, useRef, useState } from 'react';
import { QueryParamDrivenDrawerCompositeContentProvider } from './QueryParamDrivenDrawerCompositeContentProvider';
import { Logger } from '../../support/Logger';
import { QueryParamDrivenDrawerHelper } from './QueryParamDrivenDrawerHelper';
import { HEADER_HEIGHT } from '../../AppHeader';

// managed by ion-menu
const DRAWER_Z_INDEX = 1000;

// has to be > DRAWER_Z_INDEX or else ion-menu will intercept all clicks
const PREVENT_BLUR_BACKDROP_Z_INDEX = DRAWER_Z_INDEX + 1;

const useStyles = createUseStyles<'menu' | 'header' | 'title'>({
    menu: {
        '&::part(container)': {

            // currently we use the same FINAL_DRAWER_WIDTH (for the same of consistency and expediance)
            // but not sure if that makes sense moving forward
            minWidth: `calc(min(${FINAL_DRAWER_WIDTH}px, 100%))`
        },
    },
    header: {
        boxShadow: 'none',
        WebkitBoxShadow: 'none'
    },
    title: {
        textTransform: 'capitalize',

        [mediaMaxWidth.sm]: {
            fontSize: 19,
            paddingInline: '14px !important'
        }
    },
});

/**
 * Capturing some knowledge, at first DrawerPage felt highly coupled to Caremap (especially focused caremap) and the associated ion-menu
 * so I was able to get something working quickly that was essentially a fork of the things I needed
 *
 * but this didn't feel like the right path, b/c I disabled  a bunch of things I don't need yet, that we likely will want over time
 *
 * next I realized I might be able to leverage DrawerModal, but this has strong opinions about animation and backdrop, that proved more
 *  effort than they were worth to resolve
 *
 * I then noticed that DrawerContent is already a agnostic View, and so i created my own ion-menu and leveraged that
 */

export interface IMenuProps {
    onClose?: () => void;
}

export interface IQueryParamDrawerOptions {
    allowBackdropClose?: boolean;
};

interface IDrawerState {
    // this drives which drawer we are looking at
    mode: string;

    // there are query params for the specific drawer
    params?: any;

    options?: IQueryParamDrawerOptions;

    Component?: React.FC<any>
}

export const QueryParamDrivenDrawer: React.FC = () => {
    const menuRef = useRef<HTMLIonMenuElement>(null);

    const drawerContentRef = useRef<{ counter: number; }>({ counter: 1 });

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const logger = useInstance(Logger);

    const drawerContentProvider = useInstance(QueryParamDrivenDrawerCompositeContentProvider);

    const classes = useStyles();
    const history = useInstance(HistoryViewModel);
    const [drawerState, _setDrawerState] = useState<IDrawerState>({ mode: '' });

    const setDrawerState = (state: IDrawerState) => {
        // increment counter so we don't remove the content until after animation is complete
        drawerContentRef.current.counter++;

        _setDrawerState(state);
    };

    useEffect(() => {
        const onHashChange = () => {
            const drawerParamString = history.getCurrentSearchParamMap().drawer;

            if (drawerParamString){
                const params = JSON.parse(drawerParamString);
                try {
                    const { mode, allowBackdropClose, ...otherParams } = params;
                    setDrawerState({
                        mode: mode,
                        params: otherParams,
                        options: drawerContentProvider.getOptions(mode),
                        Component: drawerContentProvider.getComponent(params)
                    });
                    menuRef.current!.open();
                }
                catch (err: any){
                    logger.error(err);
                }
            }

            // if no drawer and its open, then close it
            else {

                // we have to delay removing our content until the drawer closes
                // but when we do, we don't want to remove content from a new drawer render
                // so what instance of the drawer are we closing, as we should only remove
                // the content if that is still the content being displayed
                const idToRemove = drawerContentRef.current.counter;

                Promise.resolve().then(async () => {
                    const isOpen = await menuRef.current?.isOpen();
                    if (isOpen){
                        menuRef.current?.close();
                    }
                    // TODO: if automated tests, skip this step
                    await new Promise(resolve => setTimeout(resolve, 350));

                    if (drawerContentRef.current.counter === idToRemove){
                        setDrawerState({ mode: '' });
                    }
                });
            }
        };

        history.listen(onHashChange);
        onHashChange();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, drawerContentProvider]);

    const DrawerComponent = drawerState.Component;

    return (
        <>
            {/* TODO: need an id for the main viewport */}
            <div id="my-main-content" />

            {/*
            * Mask over the the page - otherwise ion-menu implements close on blur
            * NOTE: This mask would ideally be UNDER the drawer, but ion-menu will close on any clicks on its z-index
            */}
            {!drawerState.options?.allowBackdropClose && (
                <div
                    onClick ={(e: React.MouseEvent) => {
                        // NOTE: we intentionally do nothing at this time.
                        // it would be easy to abandon your changes and dismiss the form
                        // but ideally, we would know if the form is dirty, and not abandon your changes
                        // so we're going to wait til that is a priority
                        e.stopPropagation();
                    }}
                    style={{
                        position: 'absolute',
                        // backgroundColor: 'white',
                        opacity: .2,
                        display: drawerState.mode ? 'block' : 'none',
                        top: -HEADER_HEIGHT,
                        left: 0,
                        bottom: 0,
                        zIndex: PREVENT_BLUR_BACKDROP_Z_INDEX,

                        // cannot position over drawer, or ion menu content will be masked
                        right: FINAL_DRAWER_WIDTH,
                    }}
                />
            )}
            <IonMenu
                data-id="query-param-driver-drawer"
                data-drawer-mode={drawerState.mode}
                side="end"
                contentId="my-main-content"
                type="overlay"
                swipeGesture={false}
                className={classes.menu}
                onIonWillClose={() => {
                    drawerHelper.close();
                }}
                ref={menuRef}
                menuId="query-param-driver-drawer"
            >
                {DrawerComponent && (
                    // there is runway here for us to be able to send menu specific props down
                    // but haven't done it, yet b/c YAGNI
                    // one example of a driver is on blur - the menu knows its closing
                    // but the DrawerPage leaving handler never fires
                    // but we solved this by listening to unmount (and therefore didn't need to pass anything down)
                    // so for now... still YAGNI
                    <DrawerComponent {...drawerState.params} />
                )}
            </IonMenu>

        </>
    );
};
